import React, { useState, useEffect } from "react";
import { ProjectSetup, CRMCompany, CRMPerson } from "../types";
import { DEFAULT_SETUP } from "../App";
import { Settings2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { FPT_PROJECT_STATUS_OPTIONS, getProjectStatusTone, normalizeProjectStatus } from "../lib/projectStatus";
import { cn } from "../utils";
import { MarkdownField } from "./MarkdownField";
import { SectionPillHeader } from "./SectionPillHeader";

interface Props {
  setup: ProjectSetup;
  onChange: (setup: ProjectSetup) => void;
  readOnly?: boolean;
  defaultAssumptions?: string;
}

export function ProjectSetupForm({ setup, onChange, readOnly = false, defaultAssumptions }: Props) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CRMCompany[]>([]);
  const [people, setPeople] = useState<CRMPerson[]>([]);
  const [showPersonAutocomplete, setShowPersonAutocomplete] = useState(false);
  const personAutocompleteRef = React.useRef<HTMLDivElement>(null);
  const internalPeople = React.useMemo(
    () =>
      people.filter((person) =>
        person.directoryType === "internal" ||
        person.personType === "staff" ||
        person.workspaceAccountStatus === "active" ||
        person.workspaceAccountStatus === "inactive" ||
        person.id.startsWith("workspace-") ||
        person.id.startsWith("user-")
      ),
    [people]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (personAutocompleteRef.current && !personAutocompleteRef.current.contains(event.target as Node)) {
        setShowPersonAutocomplete(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchCRMData = async (retries = 3) => {
      if (!user) return;
      try {
        const [companiesRes, peopleRes] = await Promise.all([
          fetch("/api/crm/companies", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/crm/people", { headers: { "x-user-uid": user.uid } })
        ]);

        if (companiesRes.ok) setCompanies(await companiesRes.json());
        if (peopleRes.ok) setPeople(await peopleRes.json());
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => fetchCRMData(retries - 1), 2000);
        } else {
          console.error("Failed to fetch CRM data", error);
        }
      }
    };

    fetchCRMData();
  }, [user]);

  const ensureCompanyRecord = async (name: string, companyType: "Production Company" | "Distributor") => {
    const trimmedName = name.trim();
    if (!trimmedName || readOnly || !user) return null;

    const existingCompany = companies.find(c => c.name.trim().toLowerCase() === trimmedName.toLowerCase());
    if (existingCompany) return existingCompany;

    try {
      const res = await fetch("/api/crm/companies", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify({
          name: trimmedName,
          companyType: companyType === "Distributor" ? "distributor" : "production",
          type: companyType,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      });

      if (!res.ok) throw new Error("Failed to create company");
      const newCompany = await res.json();
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    } catch (error) {
      console.error("Failed to create company record", error);
      return null;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (readOnly) return;
    const { name, value, type } = e.target;
    const parsedValue = type === 'number' ? (value === "" ? "" : Number(value)) : value;
    if (name === "clientName") {
      onChange({ ...setup, clientName: String(parsedValue), productionCompanyId: "" });
      return;
    }
    if (name === "distributorName") {
      onChange({ ...setup, distributorName: String(parsedValue), distributorId: "" });
      return;
    }
    onChange({ ...setup, [name]: parsedValue });
  };

  const handleCompanyKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && setup.clientName) {
      const match = companies.find(c => c.name.toLowerCase().startsWith(setup.clientName.toLowerCase()));
      if (match && match.name !== setup.clientName) {
        e.preventDefault();
        onChange({ ...setup, clientName: match.name });
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      await handleCompanyBlur();
      e.currentTarget.blur();
    }
  };

  const handleCompanyBlur = async () => {
    const trimmedName = setup.clientName?.trim();
    if (!trimmedName || readOnly) return;

    const company = await ensureCompanyRecord(trimmedName, "Production Company");
    if (!company) return;

    if (setup.productionCompanyId !== company.id || setup.clientName !== company.name) {
      onChange({
        ...setup,
        clientName: company.name,
        productionCompanyId: company.id
      });
    }
  };

  const handleDistributorBlur = async () => {
    const trimmedName = setup.distributorName?.trim();
    if (!trimmedName || readOnly) return;

    const company = await ensureCompanyRecord(trimmedName, "Distributor");
    if (!company) return;

    if (setup.distributorId !== company.id || setup.distributorName !== company.name) {
      onChange({
        ...setup,
        distributorName: company.name,
        distributorId: company.id
      });
    }
  };

  const handleDistributorKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleDistributorBlur();
      e.currentTarget.blur();
    }
  };

  const handlePersonKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && setup.person) {
      const match = internalPeople
        .find(p => p.name.toLowerCase().startsWith(setup.person.toLowerCase()));
      if (match && match.name !== setup.person) {
        e.preventDefault();
        onChange({ ...setup, person: match.name });
      }
    }
  };

  const handleResetAssumptions = () => {
    if (readOnly) return;
    onChange({ ...setup, assumptions: defaultAssumptions || DEFAULT_SETUP.assumptions });
  };

  const companyMatch = setup.clientName && companies.find(c => c.name.toLowerCase().startsWith(setup.clientName.toLowerCase()) && c.name !== setup.clientName);
  const personMatch = setup.person && internalPeople
    .find(p => p.name.toLowerCase().startsWith(setup.person.toLowerCase()) && p.name !== setup.person);
  return (
    <div className="flex flex-col">
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
          <SectionPillHeader
            icon={Settings2}
            title="Project Details"
            description="Configure your project metadata, production companies, and dates"
          />
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Row 1 */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project Name</label>
              </div>
              <input
                type="text"
                name="projectName"
                value={setup.projectName || ""}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Production Company</label>
              </div>
              <input
                type="text"
                name="clientName"
                list="crm-companies"
                value={setup.clientName || ""}
                onChange={handleChange}
                onKeyDown={handleCompanyKeyDown}
                onBlur={handleCompanyBlur}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
              <datalist id="crm-companies">
                {companies.map(c => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
              {companyMatch && !readOnly && (
                <div className="text-xs text-indigo-600 mt-1">
                  Press Tab to autocomplete: <span className="font-semibold">{companyMatch.name}</span>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Distributor</label>
              </div>
              <input
                type="text"
                name="distributorName"
                list="crm-distributors"
                value={setup.distributorName || ""}
                onChange={handleChange}
                onKeyDown={handleDistributorKeyDown}
                onBlur={handleDistributorBlur}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
              <datalist id="crm-distributors">
                {companies.filter(c => c.type === "Distributor" || c.companyType === "distributor").map(c => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project Bid Status</label>
              <select
                name="bidStatus"
                value={setup.bidStatus || "Quote"}
                onChange={handleChange}
                disabled={readOnly}
                className={cn(
                  "w-full px-3 py-2 text-[10pt] border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500 transition-colors",
                  (setup.bidStatus || "Quote") === "Estimate" && "bg-amber-500 text-white border-amber-500",
                  (setup.bidStatus || "Quote") === "Quote" && "bg-indigo-500 text-white border-indigo-500",
                  (setup.bidStatus || "Quote") === "Bid" && "bg-emerald-500 text-white border-emerald-500"
                )}
              >
                <option value="Estimate">Estimate</option>
                <option value="Quote">Quote</option>
                <option value="Bid">Bid</option>
              </select>
            </div>

            {/* Row 2 */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project Start (Est)</label>
              <input
                type="date"
                name="projectStartEst"
                value={setup.projectStartEst || ""}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project End (Est)</label>
              <input
                type="date"
                name="projectEndEst"
                value={setup.projectEndEst || ""}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Bid Date</label>
              <input
                type="date"
                name="bidDate"
                value={setup.bidDate || ""}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project Status</label>
              <select
                name="status"
                value={normalizeProjectStatus(setup.status)}
                onChange={handleChange}
                disabled={readOnly}
                className={cn(
                  "w-full px-3 py-2 text-[10pt] border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500 transition-colors",
                  getProjectStatusTone(setup.status),
                  !setup.status && "border-slate-300"
                )}
              >
                {FPT_PROJECT_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            {/* Row 3 */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Show Code</label>
              <input
                type="text"
                name="showCode"
                value={setup.showCode || ""}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Onset Supervision (Days)</label>
              <input
                type="number"
                name="onsetSupervision"
                min="0"
                step="1"
                value={setup.onsetSupervision || 0}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Supervision Percentage</label>
              <div className="relative">
                <input
                  type="number"
                  name="supervisionPercentage"
                  min="0"
                  max="100"
                  value={setup.supervisionPercentage || 0}
                  onChange={handleChange}
                  disabled={readOnly}
                  className="w-full pl-3 pr-8 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Producer / Estimator</label>
              </div>
              <div className="relative" ref={personAutocompleteRef}>
                <input
                  type="text"
                  name="person"
                  value={setup.person || ""}
                  onChange={(e) => {
                    handleChange(e);
                    setShowPersonAutocomplete(true);
                  }}
                  onFocus={() => setShowPersonAutocomplete(true)}
                  onKeyDown={handlePersonKeyDown}
                  disabled={readOnly}
                  placeholder="Search for a person..."
                  className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                />
                {showPersonAutocomplete && setup.person && !readOnly && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {internalPeople
                      .filter(p => p.name.toLowerCase().includes(setup.person.toLowerCase()) || p.email?.toLowerCase().includes(setup.person.toLowerCase()))
                      .map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                          onClick={() => {
                            onChange({ ...setup, person: p.name });
                            setShowPersonAutocomplete(false);
                          }}
                        >
                          {p.photoUrl ? (
                            <img src={p.photoUrl} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs shrink-0">
                              {p.name?.charAt(0) || p.email?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">{p.email}</div>
                          </div>
                        </button>
                      ))}
                    {internalPeople.filter(p => p.name.toLowerCase().includes(setup.person.toLowerCase()) || p.email?.toLowerCase().includes(setup.person.toLowerCase())).length === 0 && (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        No matching users found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {personMatch && !readOnly && !showPersonAutocomplete && (
                <div className="text-xs text-indigo-600 mt-1">
                  Press Tab to autocomplete: <span className="font-semibold">{personMatch.name}</span>
                </div>
              )}
            </div>

            {/* Row 4 */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Project Type</label>
              <select
                name="projectType"
                value={setup.projectType || "episodic"}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              >
                <option value="episodic">Episodic</option>
                <option value="feature_film">Feature Film</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Base Currency</label>
              <select
                name="currency"
                value={setup.currency}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="CAD">CAD ($)</option>
                <option value="AUD">AUD ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Target Currency</label>
              <select
                name="targetCurrency"
                value={setup.targetCurrency}
                onChange={handleChange}
                disabled={readOnly}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              >
                <option value="">None</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CAD">CAD ($)</option>
                <option value="AUD">AUD ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Exchange Rate</label>
              <input
                type="number"
                name="exchangeRate"
                min="0"
                step="0.01"
                value={setup.exchangeRate || ""}
                onChange={handleChange}
                placeholder="e.g. 1.25"
                disabled={readOnly || !setup.targetCurrency}
                className="w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
            <div className="hidden lg:block"></div>

            {/* Row 4 */}
            <div className="md:col-span-2 lg:col-span-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wider">Quote Assumptions</label>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={handleResetAssumptions}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Reset to Default
                  </button>
                )}
              </div>
              <MarkdownField
                value={setup.assumptions}
                onChange={(nextValue) => onChange({ ...setup, assumptions: nextValue })}
                disabled={readOnly}
                rows={4}
                placeholder="Enter quote assumptions in Markdown..."
              />
              <p className="mt-1 text-[10px] text-slate-500">
                Use Markdown formatting such as <span className="font-medium">`- bullet point`</span>, <span className="font-medium">**bold**</span>, and numbered lists.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
